// Pop-up "Welke prijslijst geldt voor deze calculatie?" (SPEC_wijzigingen 1.3).
// Voorselectie = de standaard van de onderaannemer. Afwijken kan enkel bevestigd worden door
// Eranda, Timen, Ervin of Jordy; andere gebruikers zien de keuze maar kunnen ze niet bevestigen.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GEVEL_VARIANT_LABEL, type GevelConfig, type GevelVariant } from '@recotex/shared';
import { api } from '../lib/api';
import { fmtEUR } from '../lib/format';

export function GevelVariantModal({
  ploegId,
  standaardVariant,
  huidigeVariant,
  magBevestigen,
  verversen,
  onBevestig,
  onSluit,
}: {
  ploegId: string;
  standaardVariant: GevelVariant;
  /** Huidige keuze op de calculatie (bij verversen); anders de standaard. */
  huidigeVariant?: GevelVariant;
  magBevestigen: boolean;
  /** Verversen laadt de actuele prijslijstversie (1.4). */
  verversen?: boolean;
  onBevestig: (config: GevelConfig) => void;
  onSluit?: () => void;
}) {
  const [keuze, setKeuze] = useState<GevelVariant>(huidigeVariant ?? standaardVariant);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const configA = useQuery({ queryKey: ['gevel-config', 'A', ploegId], queryFn: () => api.projects.gevelConfig('A', ploegId) });
  const configB = useQuery({ queryKey: ['gevel-config', 'B', ploegId], queryFn: () => api.projects.gevelConfig('B', ploegId) });
  const configs: Record<GevelVariant, GevelConfig | undefined> = { A: configA.data?.config, B: configB.data?.config };
  const afwijking = keuze !== standaardVariant;
  const kanBevestigen = !afwijking || magBevestigen;

  const bevestig = async () => {
    setBezig(true);
    setFout(null);
    try {
      const config = configs[keuze] ?? (await api.projects.gevelConfig(keuze, ploegId)).config;
      onBevestig({ ...config, bevestigd: true });
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Prijslijst laden mislukt');
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" role="dialog" aria-modal="true" aria-labelledby="gevel-variant-titel">
      <div className="card max-w-lg w-full">
        <h2 id="gevel-variant-titel" className="text-xl font-bold mb-1">Welke prijslijst geldt voor deze calculatie?</h2>
        <p className="text-sm text-secondary mb-4 normal-case tracking-normal">
          {verversen ? 'Verversen herrekent de volledige crepi-ladder met de actuele prijslijstversie.' : 'De crepi wordt progressief per schijf berekend volgens de gekozen prijslijst.'}
        </p>
        <div className="space-y-2" role="radiogroup" aria-label="Prijslijstvariant">
          {(['A', 'B'] as const).map((v) => (
            <label
              key={v}
              className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer normal-case tracking-normal ${keuze === v ? 'border-primary bg-primary-50' : 'border-border'}`}
            >
              <input type="radio" name="gevel-variant" className="mt-1" checked={keuze === v} onChange={() => setKeuze(v)} />
              <span className="flex-1">
                <span className="font-bold">{GEVEL_VARIANT_LABEL[v]}</span>
                {v === standaardVariant && <span className="chip-neutral ml-2">standaard van deze onderaannemer</span>}
                {configs[v] && (
                  <span className="block text-xs text-secondary mt-0.5 tabular-nums">
                    Crepi met isolatie {configs[v]!.ladder.metIsolatie.map((t) => fmtEUR(t)).join(' / ')} per m² ·
                    zonder {configs[v]!.ladder.zonderIsolatie.map((t) => fmtEUR(t)).join(' / ')} · steenstrips {fmtEUR(configs[v]!.steenstrips.zonderVoeg)} · versie {configs[v]!.versie}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
        {afwijking && (
          <div className="mt-3 p-3 rounded-md bg-warning-100 border-l-4 border-warning text-sm normal-case tracking-normal">
            <strong>Let op:</strong> dit wijkt af van de standaard van de onderaannemer ({standaardVariant}). De afwijking wordt gelogd.
            {!magBevestigen && <span className="block mt-1">Enkel Eranda, Timen, Ervin of Jordy kunnen een afwijkende prijslijst bevestigen.</span>}
          </div>
        )}
        {fout && <p className="text-danger text-sm mt-3">{fout}</p>}
        <div className="flex justify-end gap-2 mt-5">
          {onSluit && <button type="button" className="btn-ghost" onClick={onSluit} disabled={bezig}>Later</button>}
          <button type="button" className="btn-primary" onClick={bevestig} disabled={bezig || !kanBevestigen}>
            {bezig ? 'Laden…' : `Bevestig prijslijst ${keuze}`}
          </button>
        </div>
      </div>
    </div>
  );
}
